import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, MailCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { Wordmark } from "@/components/Wordmark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function VerifyEmailPage() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState(token ? "loading" : "resend");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [resent, setResent] = useState(false);
  const calledRef = useRef(false);

  useEffect(() => {
    if (!token || calledRef.current) return;
    calledRef.current = true;
    (async () => {
      try {
        const { data } = await api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`);
        setMessage(data.message);
        setState("success");
      } catch (err) {
        setMessage(formatApiError(err));
        setState("error");
      }
    })();
  }, [token]);

  const handleResend = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/auth/resend-verification", { email });
      toast.success(data.message);
      setResent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 auth-noise" data-testid="verify-email-page">
      <header className="p-8">
        <Link to="/login"><Wordmark /></Link>
      </header>
      <main className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md bg-white border border-slate-200 rounded-md p-10 fade-up">
          {state === "loading" && (
            <div className="text-center" data-testid="verify-loading">
              <Loader2 className="h-10 w-10 animate-spin text-blue-600 mx-auto" />
              <p className="mt-4 text-sm text-slate-500">{t("E-postanız doğrulanıyor...")}</p>
            </div>
          )}
          {state === "success" && (
            <div data-testid="verify-success">
              <CheckCircle2 className="h-12 w-12 text-green-600" />
              <h1 className="font-display text-2xl font-semibold tracking-tight text-[#0A1128] mt-6">
                {t("Doğrulama tamamlandı")}
              </h1>
              <p className="mt-3 text-sm text-slate-600">{message}</p>
              <Button asChild className="mt-8 w-full bg-blue-600 hover:bg-blue-700 text-white">
                <Link to="/login" data-testid="verify-goto-login-button">{t("Giriş Yap")}</Link>
              </Button>
            </div>
          )}
          {state === "error" && (
            <div data-testid="verify-error">
              <XCircle className="h-12 w-12 text-red-600" />
              <h1 className="font-display text-2xl font-semibold tracking-tight text-[#0A1128] mt-6">
                {t("Doğrulama başarısız")}
              </h1>
              <p className="mt-3 text-sm text-slate-600">{message}</p>
              <ResendForm email={email} setEmail={setEmail} sending={sending} resent={resent} onSubmit={handleResend} />
            </div>
          )}
          {state === "resend" && (
            <div data-testid="verify-resend">
              <MailCheck className="h-12 w-12 text-blue-600" />
              <h1 className="font-display text-2xl font-semibold tracking-tight text-[#0A1128] mt-6">
                {t("Doğrulama bağlantısı gönder")}
              </h1>
              <p className="mt-3 text-sm text-slate-600">
                {t("E-posta adresinizi girin, size yeni bir doğrulama bağlantısı gönderelim.")}
              </p>
              <ResendForm email={email} setEmail={setEmail} sending={sending} resent={resent} onSubmit={handleResend} />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function ResendForm({ email, setEmail, sending, resent, onSubmit }) {
  if (resent) {
    return (
      <div className="mt-8 rounded-md border border-green-200 bg-green-50 p-4" data-testid="resend-success">
        <p className="text-sm text-green-800">
          Bağlantı gönderildi. E-posta kutunuzu (ve spam klasörünü) kontrol edin.
        </p>
      </div>
    );
  }
  return (
    <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="resend-form">
      <div className="space-y-2">
        <Label htmlFor="resend-email">E-posta</Label>
        <Input id="resend-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="admin@firma.com" data-testid="resend-email-input" className="focus-visible:ring-blue-600" />
      </div>
      <Button type="submit" disabled={sending} data-testid="resend-submit-button"
        className="w-full bg-blue-600 hover:bg-blue-700 text-white">
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Bağlantıyı Gönder"}
      </Button>
    </form>
  );
}
